import {
  normalizeRedeemDustInTrades,
  resolveRedeemSharesForSave,
  tradeShares
} from './assetHoldingCalc';
import { ensureAutoExpectedRedeemOnTrade } from './assetRedeemEligibility';
import { clearAllAssetDerivedCaches } from './assetStorageBackup';
import {
  HOLDING_STORE_BASE_KEY,
  getAssetStorageUserId,
  migrateLegacyHoldingStoreForUser,
  scopedAssetKey
} from './assetStorageScope';

const STORE_VERSION = 1;
const MIGRATE_SHARES_6_DONE_BASE = 'asset_center_migrate_shares_6_done_v1';
const MIGRATE_PRUNE_EMPTY_DONE_BASE = 'asset_center_prune_empty_done_v1';

function storeKey() {
  // 首屏在 Auth 初始化前也可能读持仓：此处补一次旧全局键认领，避免短暂空数据
  const uid = getAssetStorageUserId();
  if (uid) migrateLegacyHoldingStoreForUser(uid);
  return scopedAssetKey(HOLDING_STORE_BASE_KEY);
}

function safeParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function round6(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return Math.round(v * 1e6) / 1e6;
}

function round2(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return Math.round(v * 100) / 100;
}

function decimalsLe2(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return false;
  return Math.abs(v * 100 - Math.round(v * 100)) < 1e-9;
}

function backfillTradesSharesTo6(trades) {
  const arr = Array.isArray(trades) ? trades : [];
  let dirty = false;
  const next = arr.map((t) => {
    if (!t) return t;
    const amount = Number(t.amount);
    const nav = Number(t.confirmNav);
    const canDerive = Number.isFinite(amount) && amount > 0 && Number.isFinite(nav) && nav > 0;
    const derived = canDerive ? (amount / nav) : null;
    const derived6 = derived != null ? round6(derived) : null;
    const derived2 = derived != null ? round2(derived) : null;
    const sharesRaw = t.shares;
    const sharesNum = (sharesRaw != null && sharesRaw !== '' && Number.isFinite(Number(sharesRaw)))
      ? Number(sharesRaw)
      : NaN;

    if (!Number.isFinite(sharesNum)) {
      if (derived6 != null) {
        dirty = true;
        return { ...t, shares: derived6 };
      }
      return t;
    }

    if (derived6 != null && derived2 != null && decimalsLe2(sharesNum)) {
      const near = Math.abs(sharesNum - derived2) < 1e-9;
      if (near && Math.abs(sharesNum - derived6) > 1e-9) {
        dirty = true;
        return { ...t, shares: derived6 };
      }
    }
    return t;
  });
  return { trades: next, dirty };
}

/**
 * 已确认买入：份额必须 = amount/confirmNav（内部 6 位，与小程序一致）。
 * 修复「只改金额未重算份额」导致的脏数据（如 2万→2千仍留旧份额）。
 */
function healConfirmedBuyShares(trades) {
  const arr = Array.isArray(trades) ? trades : [];
  let dirty = false;
  const next = arr.map((t) => {
    if (!t) return t;
    const type = t.type === 'redeem' || t.type === '赎回' ? 'redeem' : 'buy';
    if (type !== 'buy') return t;
    const st = t.status != null ? String(t.status) : 'confirmed';
    if (st === 'pending') return t;
    const amount = Number(t.amount);
    const nav = Number(t.confirmNav);
    if (!(amount > 0) || !(nav > 0)) return t;
    const derived6 = round6(amount / nav);
    if (derived6 == null) return t;
    const sharesNum = t.shares != null && t.shares !== '' ? Number(t.shares) : NaN;
    if (!Number.isFinite(sharesNum) || Math.abs(sharesNum - derived6) > 1e-6) {
      dirty = true;
      return { ...t, shares: derived6 };
    }
    return t;
  });
  return { trades: next, dirty };
}

function normalizeTrade(trade) {
  if (!trade || typeof trade !== 'object') return null;
  const type = trade.type === 'redeem' || trade.type === '赎回' ? 'redeem' : 'buy';
  const status = trade.status != null ? String(trade.status) : 'confirmed';
  return {
    ...trade,
    type,
    status
  };
}

function normalizeHolding(holding) {
  if (!holding || typeof holding !== 'object') return null;
  const trades = (Array.isArray(holding.trades) ? holding.trades : [])
    .map(normalizeTrade)
    .filter(Boolean);
  return {
    ...holding,
    holdingId: holding.holdingId ? String(holding.holdingId) : '',
    productId: holding.productId != null ? String(holding.productId) : '',
    productName: holding.productName || holding.product_name || '未知产品',
    agencyName: holding.agencyName || holding.agency_name || '',
    productCategory: holding.productCategory || holding.product_category || '',
    trades,
    updatedAt: holding.updatedAt || Date.now()
  };
}

function applyRedeemDustFix(holdings) {
  let dirty = false;
  const next = (holdings || []).map((h) => {
    const res = normalizeRedeemDustInTrades(h.trades);
    if (res.changed) dirty = true;
    return res.changed ? { ...h, trades: res.trades, updatedAt: Date.now() } : h;
  });
  return { holdings: next, dirty };
}

function runMigrations(holdings) {
  let nextHoldings = holdings.map(normalizeHolding).filter(Boolean);
  let dirty = false;

  let sharesDone = false;
  try {
    sharesDone = Boolean(window.localStorage.getItem(scopedAssetKey(MIGRATE_SHARES_6_DONE_BASE)));
  } catch (error) {
    sharesDone = false;
  }
  if (!sharesDone) {
    nextHoldings = nextHoldings.map((h) => {
      const res = backfillTradesSharesTo6(h.trades);
      if (res.dirty) dirty = true;
      return res.dirty ? { ...h, trades: res.trades } : h;
    });
    try {
      window.localStorage.setItem(scopedAssetKey(MIGRATE_SHARES_6_DONE_BASE), '1');
    } catch (error) {
      // ignore
    }
  }

  let pruneDone = false;
  try {
    pruneDone = Boolean(window.localStorage.getItem(scopedAssetKey(MIGRATE_PRUNE_EMPTY_DONE_BASE)));
  } catch (error) {
    pruneDone = false;
  }
  if (!pruneDone) {
    const beforeLen = nextHoldings.length;
    nextHoldings = nextHoldings.filter((h) => (h.trades || []).length > 0);
    if (nextHoldings.length !== beforeLen) dirty = true;
    try {
      window.localStorage.setItem(scopedAssetKey(MIGRATE_PRUNE_EMPTY_DONE_BASE), '1');
    } catch (error) {
      // ignore
    }
  }

  const dustRes = applyRedeemDustFix(nextHoldings);
  nextHoldings = dustRes.holdings;
  if (dustRes.dirty) {
    dirty = true;
    clearAllAssetDerivedCaches();
  }

  // 每次读取都纠偏：确认买入份额与金额/净值不一致
  let buySharesHealed = false;
  nextHoldings = nextHoldings.map((h) => {
    const res = healConfirmedBuyShares(h.trades);
    if (res.dirty) {
      buySharesHealed = true;
      dirty = true;
      return { ...h, trades: res.trades, updatedAt: Date.now() };
    }
    return h;
  });
  if (buySharesHealed) clearAllAssetDerivedCaches();

  return { holdings: nextHoldings, dirty };
}

export function getHoldings() {
  const stored = safeParse(window.localStorage.getItem(storeKey()));
  if (stored?.version === STORE_VERSION && Array.isArray(stored.holdings)) {
    const { holdings, dirty } = runMigrations(stored.holdings);
    if (dirty) saveHoldings(holdings);
    return holdings;
  }
  return [];
}

export function saveHoldings(holdings) {
  window.localStorage.setItem(
    storeKey(),
    JSON.stringify({ version: STORE_VERSION, holdings: holdings || [] })
  );
  try {
    window.dispatchEvent(new CustomEvent('asset-holdings-changed'));
  } catch (e) {
    // ignore
  }
}

export function exportHoldings() {
  return JSON.stringify({
    version: STORE_VERSION,
    exportedAt: new Date().toISOString(),
    holdings: getHoldings()
  }, null, 2);
}

export function importHoldings(text) {
  const parsed = safeParse(text);
  const holdings = Array.isArray(parsed) ? parsed : parsed?.holdings;
  if (!Array.isArray(holdings)) {
    throw new Error('导入文件格式不正确');
  }
  const normalized = holdings.map(normalizeHolding).filter(Boolean);
  const { holdings: migrated } = runMigrations(normalized);
  clearAllAssetDerivedCaches();
  saveHoldings(migrated);
  return migrated;
}

export function clearAllHoldings() {
  saveHoldings([]);
}

export function productKey(holding) {
  const category = holding?.productCategory ? String(holding.productCategory) : 'regular-plus';
  const pid = holding?.productId != null ? String(holding.productId) : '';
  return `${category}_${pid}`;
}

export function findHoldingById(holdings, holdingId) {
  const id = String(holdingId || '');
  return (holdings || []).find((h) => String(h.holdingId) === id) || null;
}

export function findHoldingByProduct(holdings, productId, productCategory = '', opts = {}) {
  const pid = String(productId || '');
  const cat = productCategory ? String(productCategory) : '';
  const list = holdings || [];
  if (pid) {
    if (cat) {
      const exact = list.find((h) => (
        String(h.productId) === pid && String(h.productCategory || '') === cat
      ));
      if (exact) return exact;
    }
    const byId = list.find((h) => String(h.productId) === pid);
    if (byId) return byId;
  }
  // 搜索选品 / 同步后 ID 不一致时：按产品名（可选机构）回退匹配
  const name = String(opts.productName || '').trim();
  if (!name) return null;
  const agency = String(opts.agencyName || '').trim();
  return list.find((h) => {
    if (String(h.productName || '').trim() !== name) return false;
    if (!agency) return true;
    const ha = String(h.agencyName || '').trim();
    return !ha || ha === agency;
  }) || null;
}

function newTradeId() {
  return `trade_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function newHoldingId(productId) {
  return `holding_${productId}_${Date.now()}`;
}

export function createBuyTrade(product, tradeInput) {
  const now = Date.now();
  const amount = Number(tradeInput.amount) || 0;
  const confirmNav = Number(tradeInput.confirmNav) || 0;
  const trade = {
    tradeId: newTradeId(),
    type: 'buy',
    status: 'confirmed',
    confirmDate: tradeInput.confirmDate || '',
    paymentDate: tradeInput.paymentDate || tradeInput.confirmDate || '',
    amount,
    confirmNav,
    confirmNavSourceDate: tradeInput.confirmNavSourceDate || '',
    shares: round6(amount / confirmNav),
    createdAt: now
  };
  const termDays = product?.term ?? tradeInput?.termDays ?? tradeInput?.term;
  return ensureAutoExpectedRedeemOnTrade(trade, termDays);
}

export function createRedeemTrade(tradeInput) {
  const now = Date.now();
  const shares = round6(tradeInput.shares);
  const confirmNav = Number(tradeInput.confirmNav) || 0;
  return {
    tradeId: newTradeId(),
    type: 'redeem',
    status: 'confirmed',
    confirmDate: tradeInput.confirmDate || '',
    confirmNav,
    confirmNavSourceDate: tradeInput.confirmNavSourceDate || '',
    shares,
    amount: round2(shares * confirmNav),
    createdAt: now
  };
}

export function createPendingBuyTrade(tradeInput, predicted = {}) {
  const now = Date.now();
  const amount = Number(tradeInput.amount) || 0;
  return {
    tradeId: newTradeId(),
    type: 'buy',
    status: 'pending',
    paymentDate: tradeInput.paymentDate || '',
    amount,
    expectedConfirmYmd: predicted.expectedConfirmYmd || tradeInput.expectedConfirmYmd || '',
    confirmMode: predicted.confirmMode || tradeInput.confirmMode || 'manual',
    confirmPendingReason: tradeInput.confirmPendingReason || 'await_date',
    expectedConfirmCustomized: !!tradeInput.expectedConfirmCustomized,
    createdAt: now
  };
}

export function createPendingRedeemTrade(tradeInput, predicted = {}) {
  const now = Date.now();
  const shares = round6(tradeInput.shares);
  return {
    tradeId: newTradeId(),
    type: 'redeem',
    status: 'pending',
    submitDate: tradeInput.submitDate || '',
    shares,
    expectedConfirmYmd: predicted.expectedConfirmYmd || tradeInput.expectedConfirmYmd || '',
    confirmMode: predicted.confirmMode || tradeInput.confirmMode || 'manual',
    confirmPendingReason: tradeInput.confirmPendingReason || 'await_date',
    expectedConfirmCustomized: !!tradeInput.expectedConfirmCustomized,
    createdAt: now
  };
}

export function appendPendingBuyTrade(holdings, product, tradeInput, predicted) {
  const list = Array.isArray(holdings) ? holdings.slice() : [];
  const trade = createPendingBuyTrade(tradeInput, predicted);
  const existing = findHoldingByProduct(list, product.product_id, product.product_category || '');
  if (existing) {
    return list.map((h) => (
      String(h.holdingId) === String(existing.holdingId)
        ? { ...h, trades: [trade, ...(h.trades || [])], updatedAt: Date.now() }
        : h
    ));
  }
  const holding = createHoldingFromTrade(product, { ...tradeInput, amount: tradeInput.amount, confirmDate: '', confirmNav: 0 });
  holding.trades = [trade];
  return [holding, ...list];
}

export function appendPendingBuyTradeToHolding(holdings, holdingId, product, tradeInput, predicted) {
  const list = Array.isArray(holdings) ? holdings.slice() : [];
  const trade = createPendingBuyTrade(tradeInput, predicted);
  return list.map((h) => (
    String(h.holdingId) === String(holdingId)
      ? { ...h, trades: [trade, ...(h.trades || [])], updatedAt: Date.now() }
      : h
  ));
}

export function appendPendingRedeemTrade(holdings, holdingId, tradeInput, predicted) {
  const list = Array.isArray(holdings) ? holdings.slice() : [];
  return list.map((h) => {
    if (String(h.holdingId) !== String(holdingId)) return h;
    const resolved = {
      ...tradeInput,
      shares: resolveRedeemSharesForSave(h.trades || [], tradeInput?.shares, null)
    };
    const trade = createPendingRedeemTrade(resolved, predicted);
    return { ...h, trades: [...(h.trades || []), trade], updatedAt: Date.now() };
  });
}

export function confirmTradeManually(holdings, holdingId, tradeId, patch, termDays) {
  const next = updateTrade(holdings, holdingId, tradeId, {
    ...patch,
    status: 'confirmed'
  });
  if (termDays == null || termDays === '') return next;
  return next.map((h) => {
    if (String(h.holdingId) !== String(holdingId)) return h;
    const trades = (h.trades || []).map((t) => {
      if (String(t.tradeId) !== String(tradeId)) return t;
      return ensureAutoExpectedRedeemOnTrade(t, termDays);
    });
    return { ...h, trades, updatedAt: Date.now() };
  });
}

export function createHoldingFromTrade(product, tradeInput) {
  const now = Date.now();
  const trade = createBuyTrade(product, tradeInput);
  return {
    holdingId: newHoldingId(product.product_id),
    productId: String(product.product_id || ''),
    productName: product.product_name || product.product_full_name || '未知产品',
    agencyName: product.agent_org || tradeInput.agencyName || '',
    productCategory: product.product_category || '',
    trades: [trade],
    createdAt: now,
    updatedAt: now
  };
}

export function appendBuyTradeToHolding(holdings, holdingId, product, tradeInput) {
  const list = Array.isArray(holdings) ? holdings.slice() : [];
  const trade = createBuyTrade(product, tradeInput);
  return list.map((h) => (
    String(h.holdingId) === String(holdingId)
      ? { ...h, trades: [trade, ...(h.trades || [])], updatedAt: Date.now() }
      : h
  ));
}

export function appendBuyTrade(holdings, product, tradeInput) {
  const list = Array.isArray(holdings) ? holdings.slice() : [];
  const trade = createBuyTrade(product, tradeInput);
  const existing = findHoldingByProduct(list, product.product_id, product.product_category || '');
  if (existing) {
    return list.map((h) => (
      String(h.holdingId) === String(existing.holdingId)
        ? { ...h, trades: [trade, ...(h.trades || [])], updatedAt: Date.now() }
        : h
    ));
  }
  return [createHoldingFromTrade(product, tradeInput), ...list];
}

export function appendRedeemTrade(holdings, holdingId, tradeInput) {
  const list = Array.isArray(holdings) ? holdings.slice() : [];
  return list.map((h) => {
    if (String(h.holdingId) !== String(holdingId)) return h;
    const resolved = {
      ...tradeInput,
      shares: resolveRedeemSharesForSave(h.trades || [], tradeInput?.shares, null)
    };
    const trade = createRedeemTrade(resolved);
    return { ...h, trades: [...(h.trades || []), trade], updatedAt: Date.now() };
  });
}

export function removeTrade(holdings, holdingId, tradeId) {
  const list = Array.isArray(holdings) ? holdings.slice() : [];
  return list
    .map((h) => {
      if (String(h.holdingId) !== String(holdingId)) return h;
      const trades = (h.trades || []).filter((t) => String(t.tradeId) !== String(tradeId));
      return { ...h, trades, updatedAt: Date.now() };
    })
    .filter((h) => (h.trades || []).length > 0);
}

export function removeHolding(holdings, holdingId) {
  return (holdings || []).filter((h) => String(h.holdingId) !== String(holdingId));
}

export function updateTrade(holdings, holdingId, tradeId, patch, termDays) {
  const list = Array.isArray(holdings) ? holdings.slice() : [];
  const term = termDays ?? patch?.termDays ?? patch?.term;
  return list.map((h) => {
    if (String(h.holdingId) !== String(holdingId)) return h;
    const trades = (h.trades || []).map((t) => {
      if (String(t.tradeId) !== String(tradeId)) return t;
      const next = { ...t, ...patch };
      delete next.termDays;
      delete next.term;
      const isRedeem = next.type === 'redeem' || next.type === '赎回' || t.type === 'redeem' || t.type === '赎回';
      if (isRedeem && next.shares != null) {
        next.shares = resolveRedeemSharesForSave(h.trades || [], next.shares, tradeId);
      }
      if (isRedeem && next.shares != null && next.confirmNav > 0) {
        next.amount = round2(Number(next.shares) * Number(next.confirmNav));
      }
      // 买入：与小程序一致，每次按金额/净值重算份额（内部 6 位），避免只改金额残留旧份额
      const isBuy = !isRedeem && (next.type === 'buy' || next.type === '买入' || t.type === 'buy' || t.type === '买入' || !next.type);
      if (isBuy && Number(next.amount) > 0 && Number(next.confirmNav) > 0) {
        next.shares = tradeShares(next);
      }
      // 确认买入补/刷新 auto 可赎日（不覆盖 user）；显式 user 校正原样保留
      if (isBuy && term != null && term !== '') {
        return ensureAutoExpectedRedeemOnTrade(next, term);
      }
      return next;
    });
    return { ...h, trades, updatedAt: Date.now() };
  });
}

export function listAllTrades(holdings, agencyFilter = '全部机构') {
  const rows = [];
  (holdings || []).forEach((h) => {
    const agencyName = h.agencyName || '未分组';
    if (agencyFilter !== '全部机构' && agencyName !== agencyFilter) return;
    (h.trades || []).forEach((t) => {
      rows.push({
        ...t,
        holdingId: h.holdingId,
        productId: h.productId,
        productCategory: h.productCategory,
        productName: h.productName,
        agencyName
      });
    });
  });
  return rows;
}
